import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Store, ExternalLink, ShoppingBag, CheckCircle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useCart } from '../contexts/CartContext';
import { Button } from '../components/ui/button';
import CountdownTimer from '../components/CountdownTimer';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : 'http://localhost:8000/api';

export default function CheckoutPage() {
  const { t } = useLanguage();
  const { deviceUUID, items: localItems, removeItemsByStore } = useCart();
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sessionId, setSessionId] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState({});
  const [checkoutOpenedStores, setCheckoutOpenedStores] = useState(new Map());
  // Track which stores have had checkout opened: Map<storeId, sessionId>
  const [paidStores, setPaidStores] = useState([]); // [{storeId, storeName}]


  // Fetch cart from MySQL for checkout
  useEffect(() => {
    const fetchCheckoutCart = async () => {
      try {
        // Get all cart sessions from localStorage
        const cartSessions = JSON.parse(localStorage.getItem('cart_sessions') || '{}');
        const sessionIds = Object.values(cartSessions);

        if (sessionIds.length === 0) {
          console.log('No sessions found, cart is empty');
          setLoading(false);
          return;
        }

        // Use the first session_id for backward compatibility
        setSessionId(sessionIds[0]);

        // Fetch items from ALL sessions and merge them
        const allItems = [];
        for (const sessionId of sessionIds) {
          try {
            const response = await axios.get(`${API}/cart-session/${sessionId}`);
            if (response.data.success && response.data.items.length > 0) {
              allItems.push(...response.data.items);
            }
          } catch (error) {
            console.error(`Error fetching session ${sessionId}:`, error);
          }
        }

        if (allItems.length > 0) {
          // Merge MySQL cart data with local items for product details
          const enrichedItems = allItems.map(mysqlItem => {
            const localItem = localItems.find(
              item => String(item.product_id) === String(mysqlItem.product_id)
            );
            return {
              ...mysqlItem,
              product_name: localItem?.product_name || 'Unknown Product',
              price: localItem?.price || 0,
              img_url: localItem?.img_url,
              store_name: localItem?.store_name || 'Unknown Store',
              store_alias: localItem?.store_alias || '',
              store_id: localItem?.store_id,
              unit: localItem?.unit || 'pcs'
            };
          });
          setCartItems(enrichedItems);
        } else {
          // Fallback to local items if MySQL is empty
          setCartItems(localItems);
        }
      } catch (error) {
        console.error('Error fetching checkout cart:', error);
        // Fallback to local items on error
        setCartItems(localItems);
      } finally {
        setLoading(false);
      }
    };


    fetchCheckoutCart();
  }, [deviceUUID, localItems]);

  // Poll D1 Miniweb cart status every 5 seconds for stores that have opened checkout
  useEffect(() => {
    if (checkoutOpenedStores.size === 0 || cartItems.length === 0) return;

    const checkD1CartStatus = async () => {
      try {
        for (const [storeId, storeSessionId] of checkoutOpenedStores) {
          try {
            const response = await axios.get(
              `${API}/cart/d1-status/${storeSessionId}`
            );

            if (response.data.success && !response.data.exists) {
              // Find store name before removing
              const paidStore = cartItems.find(item => item.store_id === storeId);
              const storeName = paidStore?.store_name || 'Toko';
              console.log(`🎉 Payment completed for store ${storeId} (${storeName}) - items removed from D1`);

              // Clean up MySQL session items so they aren't reused
              try {
                await axios.delete(`${API}/cart-session/${storeSessionId}/paid`);
                console.log(`✅ MySQL session cleaned up: ${storeSessionId}`);
              } catch (cleanupErr) {
                console.error('MySQL cleanup error (non-fatal):', cleanupErr);
              }

              // Add to paid stores notification
              setPaidStores(prev => [...prev, { storeId, storeName }]);

              // Remove from CartContext (updates localStorage)
              removeItemsByStore(storeId);

              // Remove items from local checkout state
              setCartItems(prev => prev.filter(item => item.store_id !== storeId));

              // Remove session from cart_sessions
              const cartSessions = JSON.parse(localStorage.getItem('cart_sessions') || '{}');
              delete cartSessions[storeId];
              localStorage.setItem('cart_sessions', JSON.stringify(cartSessions));

              // Stop polling this store
              setCheckoutOpenedStores(prev => {
                const next = new Map(prev);
                next.delete(storeId);
                return next;
              });

              setPaymentStatus(prev => ({
                ...prev,
                [storeSessionId]: { paid: true, is_expired: false }
              }));
            }
          } catch (err) {
            console.error(`Error checking D1 status for session ${storeSessionId}:`, err);
          }
        }
      } catch (error) {
        console.error('Error in D1 cart status polling:', error);
      }
    };

    checkD1CartStatus();
    const interval = setInterval(checkD1CartStatus, 5000);
    return () => clearInterval(interval);
  }, [checkoutOpenedStores, cartItems.length, deviceUUID, removeItemsByStore]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('id-ID').format(price);
  };

  const getStoreTotal = (storeItems) => {
    return storeItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const handleExpiredItem = async (productId, storeId) => {
    try {
      // Remove expired item from MySQL
      const cartSessions = JSON.parse(localStorage.getItem('cart_sessions') || '{}');
      const storeSessionId = cartSessions[storeId];

      if (storeSessionId) {
        await axios.delete(`${API}/cart-session/${storeSessionId}/item/${productId}`);
      }

      // Remove from CartContext (this updates localStorage too)
      removeItemsByStore(storeId);

      // Remove from local checkout state
      const remainingItems = cartItems.filter(item => item.store_id !== storeId);
      setCartItems(remainingItems);

      // Remove session from cart_sessions
      delete cartSessions[storeId];
      localStorage.setItem('cart_sessions', JSON.stringify(cartSessions));

      // If cart is now empty, redirect to homepage
      if (remainingItems.length === 0) {
        setTimeout(() => {
          window.location.href = '/';
        }, 1000);
      }
    } catch (error) {
      console.error('Error removing expired item:', error);
    }
  };

  const handlePayment = (storeAlias, storeId) => {
    // Get session_id (with timestamp) for this store from localStorage
    const cartSessions = JSON.parse(localStorage.getItem('cart_sessions') || '{}');
    const storeSessionId = cartSessions[storeId];

    if (!storeSessionId) {
      console.error('No session_id found for store:', storeId);
      return;
    }

    // Open checkout URL with session_id (includes timestamp)
    const checkoutUrl = `https://toko.mpstore.co.id/${storeAlias}/checkoutAI?items=${storeSessionId}`;
    window.open(checkoutUrl, '_blank', 'noopener,noreferrer');

    // Start polling D1 for this store to detect payment
    setCheckoutOpenedStores(prev => {
      const next = new Map(prev);
      next.set(storeId, storeSessionId);
      return next;
    });
  };

  // Group items by store
  const getItemsByStore = () => {
    const grouped = {};
    cartItems.forEach(item => {
      const alias = item.store_alias || 'unknown';
      if (!grouped[alias]) {
        grouped[alias] = {
          store_alias: alias,
          store_name: item.store_name,
          store_id: item.store_id,
          items: []
        };
      }
      grouped[alias].items.push(item);
    });
    return Object.values(grouped);
  };

  const getTotal = () => {
    return cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const storeGroups = getItemsByStore();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0052CC] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading cart...</p>
        </div>
      </div>
    );
  }

  if (cartItems.length === 0 && paidStores.length > 0) {
    // All items have been paid — show success completion
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-12 h-12 text-green-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            Semua Pesanan Berhasil! 🎉
          </h2>
          <p className="text-gray-500 mb-2">
            Terima kasih telah berbelanja. Segera lakukan pembayaran agar pesanan Anda diproses.
          </p>
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
            {paidStores.map((store, idx) => (
              <p key={idx} className="text-sm text-green-700 font-medium">
                ✅ {store.storeName}
              </p>
            ))}
          </div>
          <Link to="/">
            <Button className="bg-[#0052CC] hover:bg-[#0043A8]">
              Kembali ke Beranda
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center">
        <div className="text-center">
          <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <ShoppingBag className="w-12 h-12 text-gray-400" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">{t('cart_empty')}</h2>
          <p className="text-gray-500 mb-4">{t('cart_empty_desc')}</p>
          <Link to="/">
            <Button className="bg-[#0052CC] hover:bg-[#0043A8]">
              {t('continue_shopping')}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="checkout-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-4">
          <Link to="/" className="text-gray-600 hover:text-gray-900">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <h1 className="font-heading text-xl font-bold text-gray-900">
            {t('checkout_title')}
          </h1>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8">
        {/* Info Banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
          <p className="text-sm text-blue-800">
            Pesanan Anda akan diproses langsung oleh masing-masing toko. Klik tombol "Lakukan Pembayaran" untuk melanjutkan ke halaman pembayaran toko.
          </p>
        </div>

        {/* Payment Success Notifications */}
        {paidStores.length > 0 && (
          <div className="space-y-3 mb-6">
            {paidStores.map((store, idx) => (
              <div key={`paid-${store.storeId}-${idx}`} className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
                <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                <div>
                  <p className="font-bold text-green-800">
                    Terima Kasih! Pesanan dari {store.storeName} berhasil 🎉
                  </p>
                  <p className="text-sm text-green-600 mt-1">
                    Segera lakukan pembayaran agar pesanan Anda diproses oleh toko.
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Store Groups */}
        <div className="space-y-6">
          {storeGroups.map((store) => (
            <div
              key={store.store_alias}
              className="bg-white rounded-xl border border-gray-100 overflow-hidden shadow-sm"
              data-testid={`store-checkout-${store.store_alias}`}
            >
              {/* Store Header */}
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-100">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-[#0052CC]/10 rounded-lg flex items-center justify-center">
                    <Store className="w-5 h-5 text-[#0052CC]" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h2 className="font-heading font-bold text-gray-900">
                        {store.store_name}
                      </h2>
                      {/* Payment Status Badge */}
                      {(() => {
                        const cartSessions = JSON.parse(localStorage.getItem('cart_sessions') || '{}');
                        const storeSessionId = cartSessions[store.store_id];
                        const status = paymentStatus[storeSessionId];
                        if (status?.paid && !status?.is_expired) {
                          return (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                              Sudah Dibayar
                            </span>
                          );
                        }
                        return null;
                      })()}
                    </div>
                    <p className="text-sm text-gray-500">@{store.store_alias}</p>
                  </div>
                </div>
                {/* Countdown Timer - only show if not paid */}
                {(() => {
                  const cartSessions = JSON.parse(localStorage.getItem('cart_sessions') || '{}');
                  const storeSessionId = cartSessions[store.store_id];
                  const status = paymentStatus[storeSessionId];

                  if (!status?.paid && store.items[0]?.expired_time) {
                    return (
                      <CountdownTimer
                        expiredTime={store.items[0].expired_time}
                        onExpire={() => {
                          store.items.forEach(item => {
                            handleExpiredItem(item.product_id, item.store_id);
                          });
                        }}
                      />
                    );
                  }
                  return null;
                })()}
              </div>

              {/* Items */}
              <div className="p-6">
                <div className="space-y-4">
                  {store.items.map((item) => (
                    <div
                      key={item.product_id}
                      className="flex gap-4"
                      data-testid={`checkout-item-${item.product_id}`}
                    >
                      <img
                        src={item.img_url || 'https://via.placeholder.com/64'}
                        alt={item.product_name}
                        className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-gray-900 text-sm line-clamp-2">
                          {item.product_name}
                        </h3>
                        <p className="text-xs text-gray-500 mt-1">
                          {item.quantity} x Rp{formatPrice(item.price)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-gray-900">
                          Rp{formatPrice(item.price * item.quantity)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Store Subtotal */}
                <div className="border-t border-gray-100 mt-4 pt-4">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-gray-600">Subtotal ({store.items.length} item)</span>
                    <span className="text-lg font-bold text-[#0052CC]">
                      Rp{formatPrice(getStoreTotal(store.items))}
                    </span>
                  </div>

                  {/* Payment Button */}
                  {(() => {
                    const cartSessions = JSON.parse(localStorage.getItem('cart_sessions') || '{}');
                    const storeSessionId = cartSessions[store.store_id];
                    const status = paymentStatus[storeSessionId];

                    if (status?.paid && !status?.is_expired) {
                      return (
                        <Button
                          disabled
                          className="w-full bg-green-600 text-white font-bold py-3 rounded-lg flex items-center justify-center gap-2 cursor-not-allowed opacity-90"
                          data-testid={`pay-btn-${store.store_alias}`}
                        >
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          Anda sudah Membayar Produk ini
                        </Button>
                      );
                    }

                    return (
                      <Button
                        onClick={() => handlePayment(store.store_alias, store.store_id)}
                        className="w-full bg-[#FF9900] hover:bg-[#E68A00] text-white font-bold py-3 rounded-lg flex items-center justify-center gap-2"
                        data-testid={`pay-btn-${store.store_alias}`}
                      >
                        Lakukan Pembayaran
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    );
                  })()}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Grand Total */}
        {storeGroups.length > 1 && (
          <div className="bg-white rounded-xl border border-gray-100 p-6 mt-6">
            <div className="flex items-center justify-between">
              <span className="font-medium text-gray-900">Total Semua Toko</span>
              <span className="text-2xl font-bold text-[#0052CC]" data-testid="checkout-grand-total">
                Rp{formatPrice(getTotal())}
              </span>
            </div>
            <p className="text-sm text-gray-500 mt-2">
              * Pembayaran dilakukan terpisah untuk setiap toko
            </p>
          </div>
        )}

        {/* Back Button */}
        <div className="mt-8 text-center">
          <Link to="/">
            <Button variant="outline" className="rounded-full px-8">
              {t('continue_shopping')}
            </Button>
          </Link>
        </div>
      </main>
    </div>
  );
}
